import { computed, Injectable, signal } from '@angular/core';
import { ListingFileData } from "../types/listing-file-data.types";

@Injectable({
  providedIn: 'root'
})
export class ListingFilesStateService {
  private _listingFilesState = signal<Array<ListingFileData>>([]);
  public listingFilesState = computed(() =>
    [...this._listingFilesState()].sort((a, b) =>
      (a.uspPriority ?? Infinity) - (b.uspPriority ?? Infinity)
  ));
  public readonly listingFileIds = computed(() =>
    this._listingFilesState()
      .map(file => file.file.id)
      .sort((a, b) => a - b)
  );
  public rawListingFiles = computed(() => this._listingFilesState());

  public setListingFilesState(files: Array<ListingFileData>): void {
    this._listingFilesState.set(files);
  }

  public updateListingFilesState(file: ListingFileData): void {
    this._listingFilesState.update(files => {
      const existingFileIndex = files.findIndex(f => f.id === file.id);

      return existingFileIndex !== -1 ?
        files.map(f => f.id === file.id ? file : f) :
        [...files, file];
    });
  }

  public deleteFileById(fileId: number): void {
    this._listingFilesState.update(files =>
      files.filter(file => file.id !== fileId)
    );
  }

  public hasFileWithUspPriority(priority: number): boolean {
    return this.listingFilesState().some(file => file.uspPriority === priority);
  }

  public getNextFreeUspPriority(): number {
    const priorities = this.rawListingFiles()
      .map(f => f.uspPriority)
      .filter((p): p is number => p != null)
      .sort((a, b) => a - b);

    if (priorities.length === 0) { return 1; }

    if (priorities[0] !== 1) { return 1; }

    for (let i = 0; i < priorities.length - 1; i++) {
      if (priorities[i + 1] !== priorities[i] + 1) {
        return priorities[i] + 1;
      }
    }

    return priorities[priorities.length - 1] + 1;
  }

  public getRemainingFileSlots(): number {
    return 99 - this.listingFilesState().length;
  }

  public resetListingFilesState(): void {
    this._listingFilesState.set([]);
  }
}
